/** utils */
import { getNewToken, getToken } from '~/utils/auth'
import { logError } from '~/utils/log-error'

/** constants */
import { REFRESH_TOKEN } from '~/src/constants/common'
import { HTTP_STATUS_TEXT } from '~/src/constants/http'

/** types */
import type { TCommon, TGeneral } from '~/src/types/common'
import type { TGraphQlProxy, TGraphQlResponse } from '~/src/types/graphql'
import type { DocumentNode } from 'graphql'

// Constants for timeout and error handling
const DEFAULT_QUERY_TIMEOUT = 30000 // 30 seconds
const DEFAULT_MUTATION_TIMEOUT = 60000 // 60 seconds
const JWT_REGEX = /^[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+\.[A-Za-z0-9-_]*$/

const isValidJwtFormat = (token: string): boolean => {
  if (!token || typeof token !== 'string') return false
  return JWT_REGEX.test(token)
}

export class GraphQlProxy {
  protected graphQLClient: TCommon
  protected cookies: TGeneral
  protected config: TGeneral

  protected currentRetries = 0
  protected maxNumberOfRetries = 0
  protected queryTimeout: number
  protected mutationTimeout: number

  constructor({
    graphQLClient,
    $cookies = {},
    $config = {},
    options = {},
  }: TGraphQlProxy) {
    this.graphQLClient = graphQLClient
    this.maxNumberOfRetries = options.maxNumberOfRetries || 0
    this.queryTimeout = options.queryTimeout || DEFAULT_QUERY_TIMEOUT
    this.mutationTimeout = options.mutationTimeout || DEFAULT_MUTATION_TIMEOUT
    this.cookies = $cookies
    this.config = $config
  }

  addAuthorizationHeader = async ({
    $cookies = {},
    $config = {},
  }: Partial<TGraphQlProxy>) => {
    this.graphQLClient.setHeaders({}) // Reset authorization header

    const jwtToken: string = await getToken({ $cookies, $config })

    if (!jwtToken) return

    // Validate JWT format before setting header
    if (!isValidJwtFormat(jwtToken)) {
      logError(
        new Error('Invalid JWT format detected, skipping authorization header'),
      )
      return
    }

    this.graphQLClient.setHeaders({ Authorization: `Bearer ${jwtToken}` })
  }

  /**
   * Execute GraphQL request with timeout protection
   */
  async requestWithTimeout(
    query: DocumentNode,
    variables: Record<string, any> = {},
    timeout: number,
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error(`GraphQL request timeout after ${timeout}ms`))
      }, timeout)

      this.graphQLClient
        .request(query, variables)
        .then((result) => {
          clearTimeout(timeoutId)
          resolve(result)
        })
        .catch((error) => {
          clearTimeout(timeoutId)
          reject(error)
        })
    })
  }

  async query({
    query,
    variables,
  }: {
    query: DocumentNode
    variables?: Record<string, any>
  }): Promise<TGraphQlResponse> {
    const refreshToken: string = this.cookies.get(REFRESH_TOKEN)

    if (this.graphQLClient.requestConfig?.requireCredentials) {
      await this.addAuthorizationHeader({
        $cookies: this.cookies,
        $config: this.config,
      })
    }

    try {
      const data = await this.requestWithTimeout(
        query,
        variables || {},
        this.queryTimeout,
      )

      // Reset retry counter on success
      this.currentRetries = 0
      return { data }
    } catch (error: any) {
      // Check if it's a timeout error
      if (error.message?.includes('timeout')) {
        logError(new Error(`Query timeout: ${error.message}`))
        throw error
      }

      // Only retry on 500 errors if we have a refresh token and haven't exceeded retries
      if (
        error?.message === HTTP_STATUS_TEXT.INTERNAL_SERVER_ERROR &&
        refreshToken &&
        this.currentRetries < this.maxNumberOfRetries
      ) {
        this.currentRetries++
        // Log retry attempt (info level, not error)
        logError(
          new Error(
            `INFO: Retrying query (attempt ${this.currentRetries}/${this.maxNumberOfRetries})`,
          ),
          'info',
        )

        // Try to refresh the token
        const newToken = await getNewToken({
          $cookies: this.cookies,
          $config: this.config,
        })

        if (!newToken) {
          throw new Error('Failed to refresh token for retry')
        }

        // Retry the query
        return this.query({ query, variables })
      }

      // Re-throw other errors
      throw error
    }
  }

  async mutate({ mutation, variables }): Promise<TGraphQlResponse> {
    this.graphQLClient.setHeaders({}) // Reset authorization header

    try {
      const data = await this.requestWithTimeout(
        mutation,
        variables || {},
        this.mutationTimeout,
      )
      return { data }
    } catch (error: any) {
      // Check if it's a timeout error
      if (error.message?.includes('timeout')) {
        logError(new Error(`Mutation timeout: ${error.message}`))
        throw new Error(`Mutation timeout after ${this.mutationTimeout}ms`)
      }
      throw error
    }
  }
}
